import { getComponentTemplate } from './templates/componentTemplates';
import { getProjectSettings } from './settings/ProjectSettings';
import { generateUniqueId } from '../utils/generateUniqueId';
import { MCB, RCD } from '../electrical/Malzeme';

/**
 * Component Factory - Standart component'ler oluşturur
 * Template + Project Settings + User Overrides kombinasyonu
 */
export class ComponentFactory {

  /**
   * Yeni component oluşturur
   * @param {string} kind - Component türü (COMPONENT_KINDS)
   * @param {object} overrides - Kullanıcı özel ayarları
   * @returns {object} Tam yapılandırılmış component
   */
  static createComponent(kind, overrides = {}) { 

    // 1. Template'i al
    const template = getComponentTemplate(kind);
    if (!template) {
      throw new Error(`Unknown component kind: ${kind}`);
    }

    // 2. Project settings'i al
    const projectSettings = getProjectSettings();

    // 3. Project settings'e göre template'i uyarla (override'ları da gönder)
    const adaptedTemplate = this.applyProjectSettings(template, projectSettings, kind, overrides);

    // 4. Unique ID oluştur
    const componentId = overrides.id || generateUniqueId(`comp_${kind}`);

    // 5. Tüm ayarları birleştir
    const component = {
      ...adaptedTemplate,
      ...overrides,
      id: componentId,
      kind,

      // Electrical ayarları derin birleştirme
      electrical: {
        ...adaptedTemplate.electrical,
        ...overrides.electrical
      },

      // Metadata ayarları derin birleştirme
      metadata: {
        ...adaptedTemplate.metadata,
        ...overrides.metadata,
        createdAt: new Date().toISOString(),
        createdBy: 'ComponentFactory'
      },

      // Position varsayılan
      position: {
        x: 0,
        y: 0,
        z: 0,
        ...overrides.position
      },

      // Rotation varsayılan
      rotation: {
        x: 0,
        y: 0,
        z: 0,
        ...overrides.rotation
      }
    };

    // ✅ Component'e özel türetilen değerleri hesapla (entries için indexNo seçimi)
    this.calculateDerivedValues(component);

    return component;
  }

  /**
   * Project settings'e göre template'i uyarlar
   */
  static applyProjectSettings(template, projectSettings, kind, overrides = {}) {
    const adapted = JSON.parse(JSON.stringify(template)); // Deep copy
    // Genel elektriksel ayarları uygula
    if (adapted.electrical) {
      // ✅ Override'daki phaseCount varsa onu kullan, yoksa template'teki
      const finalPhaseCount = overrides.electrical?.phaseCount ?? adapted.electrical.phaseCount;

      // Proje ayarlarındaki voltaj değerlerini uygula
      if (finalPhaseCount === 3) {
        adapted.electrical.voltage = projectSettings.electrical.mainVoltage;
      } else {
        adapted.electrical.voltage = projectSettings.electrical.singlePhaseVoltage;
      }

      // Purpose of use uygula
      adapted.electrical.purposeOfUse = projectSettings.electrical.purposeOfUse;
      adapted.electrical.demandFactor = projectSettings.electrical.demandFactor;

      // Power factor uygula
      if (adapted.electrical.powerFactor !== undefined) {
        adapted.electrical.powerFactor = projectSettings.electrical.powerFactor;
      }
    }

    // Component tipine özel ayarları uygula
    switch (kind) {
      case 'supply':
        // Supply için özel ayarlar
        if (adapted.electrical) {
          adapted.electrical.voltage = projectSettings.electrical.mainVoltage;
          adapted.electrical.phaseCount = projectSettings.electrical.defaultPhaseCount;
        }
        break;

      case 'meter_panel':
        // Main Panel için özel ayarlar
        if (projectSettings.components?.mainPanel) {
          const mainPanelSettings = projectSettings.components.mainPanel;

          if (adapted.electrical) {
            adapted.electrical.voltage = mainPanelSettings.defaultVoltage;
            adapted.electrical.phaseCount = mainPanelSettings.defaultPhaseCount;
          }

          if (adapted.metadata) {
            adapted.metadata.ipRating = mainPanelSettings.ipRating;
            adapted.metadata.panelType = mainPanelSettings.panelType;
            adapted.metadata.installationType = mainPanelSettings.installationType;
          }
        }
        break;

      case 'dist_panel':
      case 'panel':
        // Panel için özel ayarlar
        if (projectSettings.components?.panels) {
          const panelSettings = projectSettings.components.panels;

          if (adapted.electrical) {
            adapted.electrical.voltage = panelSettings.defaultVoltage;
            adapted.electrical.phaseCount = panelSettings.defaultPhaseCount;
          }

          if (adapted.metadata) {
            adapted.metadata.ipRating = panelSettings.ipRating;
            adapted.metadata.panelType = panelSettings.panelType;
            adapted.metadata.installationType = panelSettings.installationType;
          }
        }
        break;

      case 'load':
      case 'branch':
        // Load için özel ayarlar
        if (projectSettings.components?.loads) {
          const loadSettings = projectSettings.components.loads;

          if (adapted.electrical) {
            adapted.electrical.voltage = loadSettings.defaultVoltage;
            adapted.electrical.phaseCount = loadSettings.defaultPhaseCount;
            adapted.electrical.powerFactor = loadSettings.defaultPowerFactor;
          }
        }
        break;
    }

    return adapted;
  }

  /**
   * Component için türetilen değerleri hesapla
   * Özellikle entries'deki MCB ve RCD indexNo'larını otomatik seç
   */
  static calculateDerivedValues(component) {

    // Entries varsa, her entry için indexNo hesapla
    if (component.entries && component.entries.length > 0) {
      component.entries.forEach(entry => {
        // MCB için indexNo seçimi
        if ((entry.kind === 'main_breaker' || entry.kind === 'breaker') && entry.type === 'MCB') {
          if (!entry.indexNo && component.electrical?.phaseCount) {
            const phaseCount = component.electrical.phaseCount;
            const minCurrent = entry.amperage || 25;
            const curve = 'C'; // Sayaç ve panel giriş sigortaları C tipi
            const shortCircuitCurCap = entry.kind === 'main_breaker' ? 10 : 6; // Ana: 10kA, Panel: 6kA

            entry.indexNo = this.selectMCBIndexNo(
              phaseCount,
              minCurrent,
              shortCircuitCurCap,
              curve,
              'AOS'
            );

      
          }
        }

        // RCD için indexNo seçimi
        if ((entry.kind === 'main_protective_device' || entry.kind === 'protective_device') && entry.type === 'RCD') {
          if (!entry.indexNo && component.electrical?.phaseCount) {
            const phaseCount = component.electrical.phaseCount;
            const minCurrent = entry.amperage || 25;
            const sensitivity = entry.kind === 'main_protective_device' ? 300 : 30; // Ana: 300mA, Panel: 30mA
            const curve = 'A'; // RCD'ler genelde A tipi

            entry.indexNo = this.selectRCDIndexNo(
              phaseCount,
              minCurrent,
              sensitivity,
              curve
            );

         
          }
        }
      });
    }
  }

  /**
   * MCB özelliklerine göre MCB dizisinden uygun indexNo'yu seçer
   * @param {number} phaseCount - Faz sayısı (1 veya 3)
   * @param {number} minCurrent - Minimum akım (A)
   * @param {number} shortCircuitCurCap - Minimum kısa devre kesme kapasitesi (kA)
   * @param {string} curve - Kesici eğrisi ('B', 'C', 'D')
   * @param {string} type - Kesici tipi ('AOS')
   */
  static selectMCBIndexNo(phaseCount, minCurrent = 25, shortCircuitCurCap = 10, curve = 'C', type = 'AOS') {
    // Polar sayısı hesaplama
    // Main breaker (sayaç sigortası): 1F → 1P, 3F → 3P (nötr kesmeyen)
    const polar = phaseCount;

    const mcb = MCB.find((obj) =>
      obj &&
      obj.polar === polar &&
      obj.current >= minCurrent &&
      obj.shortCircuitCurCap >= shortCircuitCurCap &&
      obj.curve === curve &&
      obj.type === type
    );

    if (mcb) {
      const indexNo = MCB.indexOf(mcb);
      return indexNo;
    }

    return null;
  }

  /**
   * RCD özelliklerine göre RCD dizisinden uygun indexNo'yu seçer
   * @param {number} phaseCount - Faz sayısı (1 veya 3)
   * @param {number} minCurrent - Minimum akım (A)
   * @param {number} sensitivity - Hassasiyet (mA): 30 veya 300
   * @param {string} curve - RCD tipi ('A', 'AC', 'F')
   */
  static selectRCDIndexNo(phaseCount, minCurrent = 25, sensitivity = 30, curve = 'A') {
    // RCD için polar hesaplama: 1F → 2P, 3F → 4P (RCD'ler nötr dahil)
    const polar = phaseCount === 1 ? 2 : 4;

    const rcd = RCD.find((obj) =>
      obj &&
      obj.polar === phaseCount &&
      obj.current >= minCurrent &&
      obj.sensitivity === sensitivity &&
      obj.curve === curve
    );

    if (rcd) {
      const indexNo = RCD.indexOf(rcd);
      return indexNo;
    }

    return null;
  }

  /**
   * Makine component'i oluşturur (özel güç ayarları ile)
   */
  static createMachine(power, overrides = {}) {
    const projectSettings = getProjectSettings();
    const machinePowers = projectSettings.components?.loads?.machinePowers;

    // Güce göre faz sayısını belirle
    const phaseCount = power > projectSettings.electrical.singlePhaseLimit ? 3 : 1;
    const voltage = phaseCount === 3
      ? projectSettings.electrical.mainVoltage
      : projectSettings.electrical.singlePhaseVoltage;

    return this.createComponent('load', {
      electrical: {
        installedPower: power,
        voltage,
        phaseCount
      },
      metadata: {
        loadType: 'machine',
        machineSize: this.getMachineSizeByPower(power, machinePowers)
      },
      ...overrides
    });
  }

  /**
   * Priz component'i oluşturur
   */
  static createSocket(power, overrides = {}) {
    const projectSettings = getProjectSettings();
    const socketPowers = projectSettings.components?.loads?.socketPowers;

    return this.createComponent('load', {
      electrical: {
        installedPower: power || socketPowers?.standard || 3520,
        voltage: projectSettings.electrical.singlePhaseVoltage,
        phaseCount: 1
      },
      metadata: {
        loadType: 'socket',
        socketType: power > socketPowers?.standard ? 'heavy' : 'standard'
      },
      ...overrides
    });
  }

  /**
   * Aydınlatma anahtarı component'i oluşturur
   */
  static createLightSwitch(power = 0, overrides = {}) {
    const projectSettings = getProjectSettings();

    return this.createComponent('load', {
      electrical: {
        installedPower: power,
        voltage: projectSettings.electrical.singlePhaseVoltage,
        phaseCount: 1
      },
      metadata: {
        loadType: 'lightSwitch',
        switchType: overrides.metadata?.switchType || 'normal'
      },
      ...overrides
    });
  }

  /**
   * Aydınlatma armatürü (fixture) component'i oluşturur
   */
  static createLightFixture(power = 0, overrides = {}) {
    const projectSettings = getProjectSettings();

    return this.createComponent('load', {
      electrical: {
        installedPower: power,
        voltage: projectSettings.electrical.singlePhaseVoltage,
        phaseCount: 1
      },
      metadata: {
        loadType: 'lightFixture',
        fixtureType: overrides.metadata?.fixtureType || 'normal'
      },
      ...overrides
    });
  }

  /**
   * Panel oluşturur (tip ve parent'a göre)
   */
  static createPanel(panelType, parentComponentId, overrides = {}) {
    const component = this.createComponent(panelType, {
      parentComponentId,
      ...overrides
    });

    // Panel'e varsayılan entries ekle (template'den geliyor ama emin olmak için)
    if (!component.entries || component.entries.length === 0) {
      component.entries = this.createDefaultEntries(panelType);

      // ✅ Entries eklendikten sonra indexNo'ları hesapla
      this.calculateDerivedValues(component);
    }

    return component;
  }

  /**
   * Güce göre makine boyutunu belirler
   */
  static getMachineSizeByPower(power, machinePowers) {
    if (!machinePowers) return 'medium';

    if (power <= machinePowers.small) return 'small';
    if (power <= machinePowers.medium) return 'medium';
    return 'large';
  }

  /**
   * Panel tipi için varsayılan entries oluşturur
   * ⚠️ indexNo artık hardcoded değil, calculateDerivedValues tarafından otomatik seçilecek
   */
  static createDefaultEntries(panelType) {
    const entries = [];
    const projectSettings = getProjectSettings();
    switch (panelType) {
      case 'meter_panel':
        const mainPanelSettings = projectSettings.components?.mainPanel;
        entries.push({
          id: generateUniqueId('entry'),
          kind: 'main_breaker',
          type: mainPanelSettings?.breaker?.type || 'MCB',
          // indexNo kaldırıldı - calculateDerivedValues'te hesaplanacak
          position: { x: 0, y: 0 },
          amperage: mainPanelSettings?.breaker?.defaultAmperage || 25,
          description: 'Ana Sigorta',
          title: '',
          defaultTitle: `${mainPanelSettings?.breaker?.defaultAmperage || 25}A ${mainPanelSettings?.breaker?.type || 'MCB'}`
        });
        entries.push({
          id: generateUniqueId('entry'),
          kind: 'main_protective_device',
          type: mainPanelSettings?.rcd?.type || 'RCD',
          // indexNo kaldırıldı - calculateDerivedValues'te hesaplanacak
          position: { x: 0, y: 0 },
          amperage: mainPanelSettings?.rcd?.defaultAmperage || 25,
          description: 'Ana Koruma',
          title: '',
          defaultTitle: `${mainPanelSettings?.rcd?.sensitivity || 300}mA ${mainPanelSettings?.rcd?.type || 'RCD'}`
        });
        break;

      case 'dist_panel':
        const panelSettings = projectSettings.components?.panels;
        entries.push({
          id: generateUniqueId('entry'),
          kind: 'main_breaker',
          type: panelSettings?.breaker?.type || 'MCB',
          // indexNo kaldırıldı - calculateDerivedValues'te hesaplanacak
          position: { x: 0, y: 0 },
          amperage: panelSettings?.breaker?.defaultAmperage || 25,
          description: 'Giriş Sigortası',
          title: '',
          defaultTitle: `${panelSettings?.breaker?.defaultAmperage || 25}A ${panelSettings?.breaker?.type || 'MCB'}`
        });
        entries.push({
          id: generateUniqueId('entry'),
          kind: 'protective_device',
          type: panelSettings?.rcd?.type || 'RCD',
          // indexNo kaldırıldı - calculateDerivedValues'te hesaplanacak
          position: { x: 0, y: 0 },
          amperage: panelSettings?.rcd?.defaultAmperage || 25,
          description: 'Koruma',
          title: '',
          defaultTitle: `${panelSettings?.rcd?.sensitivity || 30}mA ${panelSettings?.rcd?.type || 'RCD'}`
        });
        break;

      case 'panel':
        const subPanelSettings = projectSettings.components?.panels;
        entries.push({
          id: generateUniqueId('entry'),
          kind: 'breaker',
          type: subPanelSettings?.breaker?.type || 'MCB',
          // indexNo kaldırıldı - calculateDerivedValues'te hesaplanacak
          position: { x: 0, y: 0 },
          amperage: subPanelSettings?.breaker?.defaultAmperage || 16,
          description: 'Giriş',
          title: '',
          defaultTitle: `${subPanelSettings?.breaker?.defaultAmperage || 16}A ${subPanelSettings?.breaker?.type || 'MCB'}`
        });
        entries.push({
          id: generateUniqueId('entry'),
          kind: 'protective_device',
          type: subPanelSettings?.rcd?.type || 'RCD',
          // indexNo kaldırıldı - calculateDerivedValues'te hesaplanacak
          position: { x: 0, y: 0 },
          amperage: subPanelSettings?.rcd?.defaultAmperage || 25,
          description: 'Koruma',
          title: '',
          defaultTitle: `${subPanelSettings?.rcd?.sensitivity || 30}mA ${subPanelSettings?.rcd?.type || 'RCD'}`
        });
        break;
    }

    return entries;
  }

  /**
   * Component'i validate eder ve eksik alanları tamamlar
   */
  static validateComponent(component) {
    const errors = [];
    const warnings = [];

    // Zorunlu alanları kontrol et
    if (!component.id) errors.push('Component ID is required');
    if (!component.kind) errors.push('Component kind is required');

    // Electrical alanlarını kontrol et
    if (component.electrical) {
      if (component.electrical.voltage <= 0) {
        warnings.push('Invalid voltage value');
      }
      if (component.electrical.phaseCount < 1 || component.electrical.phaseCount > 3) {
        warnings.push('Invalid phase count');
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }
}

// Convenience methods
export const createSupply = (overrides) => ComponentFactory.createComponent('supply', overrides);
export const createMeterPanel = (parentId, overrides) => ComponentFactory.createPanel('meter_panel', parentId, overrides);
export const createDistPanel = (parentId, overrides) => ComponentFactory.createPanel('dist_panel', parentId, overrides);
export const createMachine = (power, overrides) => ComponentFactory.createMachine(power, overrides);
export const createSocket = (power, overrides) => ComponentFactory.createSocket(power, overrides);